<template>
  <v-card class="admin application-chat">
    <v-card-title flat>
      <v-badge right>
        <v-icon left>mdi-message</v-icon> <span class="title">Conversation</span>
      </v-badge>
    </v-card-title>
    <v-divider />

    <div
      class="py-1"
      style="height: 300px; overflow-y: scroll; overflow-x: hidden;"
      ref="chats-container"
    >
      <div v-if="!orderedChats[0]" class="px-3 py-2">No messages yet</div>

      <div v-for="(group, day) in dateGroupedChats" :key="`chat-group-${day}`">
        <v-chip small class="caption">
          {{ new Date(day) | date('d MMM yyyy') }}
        </v-chip>

        <div
          v-for="(message, i) in group"
          :key="`activity-${i}`"
          class="container"
          :class="{ alt: isMe(message.sender) }"
        >
          <div class="chat-avatar hidden-xs-only">
            <v-tooltip bottom>
              <v-avatar slot="activator" size="32">
                <v-img
                  contain
                  max-width="32"
                  :src="message.sender.avatar || '/img/missing/avatars/original.png'"
                />
              </v-avatar>
              <span> {{ message.sender.fullName }} </span>
            </v-tooltip>
          </div>

          <div class="chat-content text-xs-left">

            <div class="admin-note text-xs-left primary white--text">
              <span>
                <v-icon color="primary white--text">
                  mdi-triangle
                </v-icon>
              </span>
              <div>
                <markdown :html="false" :source="message.content" class="markdown" />
              </div>
            </div>

            <div
              class="white--text text-xs-right time-stamp"
              style="font-size: 9px;"
            >
              <span>
                <v-tooltip bottom v-if="message.readAt">
                  <v-icon slot="activator" small color="white"> mdi-eye </v-icon>
                  <span>Read at {{ new Date(message.readAt) | date("HH:mm 'on' d MMM yyyy") }}</span>
                </v-tooltip>

                {{ new Date(message.createdAt) | date('HH:mm') }}
              </span>
            </div>
          </div>
        </div>
      </div>
      <div v-if="savingMessage && savingMessage.length">
        <div class="caption" color="grey">
          {{ new Date() | date('d MMM yyyy') }}
        </div>

        <div class="container alt">
          <div class="chat-avatar hidden-xs-only">
            <v-progress-circular size="32" indeterminate color="success">
              <v-avatar size="30">
                <img
                  :src="user.avatar || 'img/missing/avatars/original.png'"
                />
              </v-avatar>
            </v-progress-circular>
          </div>

          <div class="chat-content text-xs-left" style="max-width: 100%;">
            <div class="client-note text-xs-left primary white--text">
              <span> <v-icon color="primary">mdi-triangle</v-icon> </span>
              <markdown :html="false" :source="savingMessage" class="markdown" />
            </div>

            <div class="text-xs-right time-stamp white--text" style="font-size: 9px;">
              <span>Sending...</span>
            </div>
          </div>
        </div>
      </div>
    </div>
    <v-divider />
    <v-card-actions class="px-0 py-0">
      <v-textarea box
        placeholder="Type to message candidate (visible to clients)"
        v-model="newMessage"
        :rows="1"
        append-icon="mdi-send"
        @click:append="saveMessage"
        @keyup.enter.exact="saveMessage"
        :value="true ? 'true' : 'false'"
        color="primary"
        style="margin-bottom: -27px; border-radius: 0px;"
      />
    </v-card-actions>
  </v-card>
</template>

<script>
// Components
import Markdown from 'vue-markdown'

// Helpers
import colors from '@/data/colors'
import _ from 'lodash'

export default {
  props: { application: Object },

  mounted() {
    this.scrollDown()
  },

  components: { Markdown },

  data() {
    return {
      newMessage: '',
      savingMessage: '',
      colors,
    }
  },

  computed: {
    user() {
      return this.$store.getters['auth/user']
    },

    orderedChats() {
      if (!this.application || !this.application.chat || !this.application.chat[0])
        return []

      const chats = JSON.parse(JSON.stringify(this.application.chat))

      return chats
        .sort(({ createdAt: a }, { createdAt: b }) => {
          if (new Date(a) > new Date(b)) return 1
          if (new Date(a) < new Date(b)) return -1
          return 0
        })
    },

    dateGroupedChats() {
      if (!this.orderedChats[0]) return []

      return _.groupBy(this.orderedChats, a => a.createdAt.substring(0, 10))
    },
  },

  watch: {
    application: {
      deep: true,
      handler: function(value) {
        this.scrollDown()
      },
    },
  },

  methods: {
    isLast: (index, array) => array.length === index + 1,

    isMe(user) {
      if (_.get(this, 'user.id') && _.get(user, 'id')) return this.user.id === user.id
    },

    isFromOtherOpportunity(activity) {
      if (_.get(this.application, 'opportunity.id') && _.get(activity, 'opportunity.id')) {
        return this.application.opportunity.id !== activity.opportunity.id
      }
      return false
    },

    scrollDown() {
      this.$nextTick(() => {
        const container = this.$refs['chats-container']
        if (container) container.scrollTop = container.scrollHeight
      })
    },

    saveMessage() {
      this.savingMessage = this.newMessage.length > 0 ? this.newMessage.trim() : this.newMessage
      this.newMessage = ''
      if (this.savingMessage.length > 0) {
        this.$nextTick(() => {
          this.scrollDown()
        })
        this.$http
          .post(`admin/applications/${this.application.id}/chats`, {
            content: this.savingMessage,
          })
          .then(
            response => {
              this.$store.dispatch('snackbarSuccess', 'Message sent')
              return this.$store
                .dispatch('admin/applications/fetch', this.application.id)
                .then(response => {
                  this.savingMessage = ''
                  this.scrollDown()
                })
            },
            errors => {
              this.$store.dispatch('snackbarError', 'Message sending failed')
              this.newMessage = this.savingMessage
              this.savingMessage = ''
              this.scrollDown()
            }
          )
      }
    },
  },
}
</script>

<style lang="scss">
.admin.application-chat {

  .container {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    padding: 0.1rem 0.7rem;
    margin: 0 auto;
    max-width: 100%;

    .chat-avatar {
      padding-right: 0.8rem;
      padding-left: 0;
    }

    & .chat-content {
      box-sizing: border-box;
      position: relative;
      padding: 0.3rem 10% 0.3rem 0;
      min-width: 50%;

      > div,
      > div:first-child {
        border-radius: 5px;
        line-height: 1rem;
        padding: 0.3rem 1.0rem 0.8rem 0.8rem;
      }

      & .time-stamp {
        opacity: 0.7;
        padding: 0.4rem 0.2rem 0.2rem 0.4rem;
        margin-top: -1.5rem;
      }

      & div.admin-note {
        border-radius: 5px;
        padding: 0.4rem 1rem 0.5rem 0rem;
        opacity: 1;

        > div {
          padding: 0 1.8rem 0.5rem 0.8rem;
        }

        & div.markdown > p {
          margin-bottom: 0px;
        }

        & .v-icon {
          position: absolute;
          transform: translateY(-70%) rotate(30deg);
          top: 50%;
          left: -0.5rem;
        }
      }
    }
  }

  & .container.alt {
    flex-direction: row-reverse;

    & .chat-avatar {
      padding-left: 0.8rem;
      padding-right: 0;
    }

    & .chat-content {
      padding-left: 10%;
      padding-right: 0%;
    }

    & div.admin-note {
      & .v-icon {
        position: absolute;
        transform: translateY(-70%) rotate(-30deg);
        top: 50%;
        right: -0.5rem;
        left: auto;
      }
    }
  }
}
</style>
